<template>
  <div>
    <b-sidebar
      id="sidebar-bill-details"
      sidebar-class="sidebar-lg"
      :visible="billDetailsSidebar.visible"
      bg-variant="white"
      shadow
      backdrop
      no-header
      right
      @shown="onShow"
      @change="(val) => mutateBillDetailsSidebar({ visible: val })"
      @hidden="clear"
    >
      <template #default="{ hide }">
        <!-- Header -->
        <div
          class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
        >
          <h4>Detalhes da solicitação</h4>
          <div>
            <feather-icon
              class="ml-1 cursor-pointer"
              icon="XIcon"
              size="16"
              @click="hide"
            />
          </div>
        </div>
        <div class="m-2" v-if="bill">
          <template v-if="!isExpense && !rollingView">
            <b-row>
              <b-col cols="6" class="mb-1"> Tipo contrato: </b-col>
              <b-col cols="6" class="mb-1">
                <span v-if="bill.user && bill.user.department_id">
                  Colaborador
                </span>
                <span v-else> 
                  PN 
                </span>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="6" class="mb-1"> Solicitante: </b-col>
              <b-col cols="6" class="mb-1 text-primary">
                <b> {{ bill.consultant.name }} </b>
              </b-col>
            </b-row>
          </template>
          <b-row>
            <b-col cols="6" class="mb-1"> Status da solicitação: </b-col>
            <b-col cols="6" class="mb-1">
              {{ bill.status | withdrawalStatus }}
            </b-col>
            <template v-if="!rollingView">
              <b-col cols="6" class="mb-1"> Data da solicitação: </b-col>
              <b-col cols="6" class="mb-1">
                {{ bill.created_at | onlyDate }}
              </b-col>
            </template>
            <b-col cols="6" class="mb-1"> Data do pagamento: </b-col>
            <b-col cols="6" class="mb-1">
              {{ bill.withdrawal ? bill.withdrawal.created_at : "" | dateTime }}
            </b-col>
            <b-col cols="6" class="mb-1"> Data de faturamento: </b-col>
            <b-col cols="6" class="mb-1">
              {{
                bill.withdrawal ? bill.withdrawal.billing_date : "" | dateTime
              }}
            </b-col>
            <template v-if="!rollingView">
              <b-col cols="6" class="mb-1" style="font-weight: bold"> Valor: </b-col>
              <b-col cols="6" class="mb-1 text-primary" >
                {{ bill.amount | toCurrency }}
              </b-col>
            </template>
          </b-row>
          <!--
            Mesma tela da divisão da rolagem do "Avaliar solicitação" (card 1219573563390460), só
            para consulta: depois de aprovada, esta é a única tela da solicitação, e o Financeiro
            confere aqui as duas transferências que fez. Solicitante, data, Pix, valor e nota fiscal
            vêm dentro dela. Sem divisão gravada, o layout de hoje abaixo continua intocado.
          -->
          <payment-rolling-split
            v-if="rollingView"
            class="mt-1"
            :bill="bill"
            :view="rollingView"
            readonly
            :can-download-document="canManage"
            :can-open-adjustments="canOpenHubAdjustments"
            :adjustments-route="adjustmentsRoute"
            @download="download(bill.id)"
          />
          <template v-else-if="!isExpense">
            <b-row class="border-bottom border-top">
              <b-col cols="12" class="mb-1 mt-1">
                <b>Detalhes da fatura</b>
              </b-col>
              <b-col cols="6" class="mb-1"> Total de entrevista paga: </b-col>
              <b-col
                cols="6"
                class="mb-1"
                data-testid="bill-details-interviews-amount"
              >
                <span>
                  {{ bill.interviews_amount | toCurrency }}
                </span>
              </b-col>
              <b-col cols="6" class="mb-1"> Total de assinatura: </b-col>
              <b-col
                cols="6"
                class="mb-1"
                data-testid="bill-details-subscriptions-amount"
              >
                <span>
                  {{ bill.subscriptions_amount | toCurrency }}
                </span>
              </b-col>
              <!--
                ⚠️ Só os saques criados depois da separação por CNPJ têm as três linhas. Nos
                antigos as colunas vêm NULL e o ajuste da Plataforma está embutido em
                adjustments_amount: quebrar o total ali mostraria um "Total Futuro Hub" menor do
                que o que foi de fato pago naquele saque.
              -->
              <template v-if="hasPayingCompanySplit">
                <b-col cols="6" class="mb-1"> Ajuste plataforma Futuro: </b-col>
                <b-col
                  cols="6"
                  class="mb-1"
                  data-testid="bill-details-platform-adjustments-amount"
                >
                  <span>
                    {{ platformAdjustmentsAmount | toCurrency }}
                  </span>
                </b-col>
              </template>
              <b-col cols="6" class="mb-2" style="font-weight: bold"> Total plataforma Futuro: </b-col>
              <b-col
                cols="6"
                class="mb-2"
                data-testid="bill-details-platform-total-amount"
              >
                <span>
                  {{ platformTotalAmount | toCurrency }}
                </span>
              </b-col>
              <b-col cols="6" class="mb-1">
                Total de solução financeira:
              </b-col>
              <b-col
                cols="6"
                class="mb-1"
                data-testid="bill-details-financial-solutions-amount"
              >
                <span>
                  {{ bill.financial_solutions_amount | toCurrency }}
                </span>
              </b-col>
              <template v-if="hasPayingCompanySplit">
                <b-col cols="6" class="mb-1"> Ajuste Futuro Hub: </b-col>
                <b-col
                  cols="6"
                  class="mb-1"
                  data-testid="bill-details-hub-adjustments-amount"
                >
                  <span>
                    {{ hubAdjustmentsAmount | toCurrency }}
                  </span>
                </b-col>
              </template>
              <template v-else>
                <b-col cols="6" class="mb-1"> Total de ajuste: </b-col>
                <b-col
                  cols="6"
                  class="mb-1"
                  data-testid="bill-details-adjustments-amount"
                >
                  <span>
                    {{ bill.adjustments_amount | toCurrency }}
                  </span>
                </b-col>
              </template>
              <b-col cols="6" class="mb-2" style="font-weight: bold"> Total Futuro Hub: </b-col>
              <b-col
                cols="6"
                class="mb-2"
                data-testid="bill-details-hub-total-amount"
              >
                <span>
                  {{ hubTotalAmount | toCurrency }}
                </span>
              </b-col>
              <!--
                ⚠️ Só com separação por CNPJ. No saque legado os ajustes estão todos sem
                `paying_company_id`, então um link filtrando por Futuro Hub abriria lista vazia —
                pior que não ter link, porque sugere que os lançamentos sumiram.

                A condição de permissão é a mesma do router e do middleware
                `fixed_financial_menu_role`: esta tela é liberada por `financial_management`, que é
                outro resource — quem entra aqui não necessariamente entra na lista de ajustes.
              -->
              <b-col
                v-if="canOpenHubAdjustments"
                cols="12"
                class="mb-2 text-right"
              >
                <b-link
                  :to="hubAdjustmentsRoute"
                  target="_blank"
                  title="Abre a lista de ajustes filtrada por este PN e pela Futuro Hub. A lista não tem filtro de competência."
                  data-testid="bill-details-hub-adjustments-link"
                >
                  <small>
                    Ver lançamentos de ajuste
                    <feather-icon icon="ExternalLinkIcon" size="12" />
                  </small>
                </b-link>
              </b-col>
              <template v-if="hasPayingCompanySplit">
                <b-col cols="6" class="mb-1"> Rolagem e outros ajustes: </b-col>
                <b-col
                  cols="6"
                  class="mb-1"
                  data-testid="bill-details-other-adjustments-amount"
                >
                  <span>
                    {{ otherAdjustmentsAmount | toCurrency }}
                  </span>
                </b-col>
              </template>

              <b-col cols="6" class="mb-1" style="font-weight: bold"> Total da fatura: </b-col>
              <b-col
                cols="6"
                class="mb-1"
                data-testid="bill-details-invoice-total-amount"
              >
                <span>
                  {{ invoiceTotal | toCurrency }}
                </span>
              </b-col>
            </b-row>
          </template>
          <template v-if="expenses && expenses.length > 0">
            <b-row class="border-bottom border-top">
              <template v-for="expense in expenses">
                <b-col cols="6" class="mb-1 mt-1">
                  <b>Despesa:</b>
                </b-col>
                <b-col cols="6" class="mb-1 mt-1"> {{ expense.name }} </b-col>
                <b-col cols="6" class="mb-1">Centro de custo:</b-col>
                <b-col cols="6" class="mb-1">{{ expense.cost_center.name }} </b-col>
                <b-col cols="6" class="mb-1">Categoria:</b-col>
                <b-col cols="6" class="mb-1">{{ expense.cost_center_category.name }} </b-col>
                <b-col cols="6" class="mb-1">Valor:</b-col>
                <b-col 
                  cols="6"
                  class="mb-1 text-primary"
                  style="font-weight: bold"
                >{{ expense.amount | toCurrency }} </b-col>
              </template>
            </b-row>
          </template>
          <b-row class="mt-1">
            <template v-if="isExpense">
              <b-col cols="6" class="mb-1"> Método de pagamento: </b-col>
              <b-col cols="6" class="mb-1"> {{ expenses[0].payment_type.name }} </b-col>
              <b-col cols="6" class="mb-1"> Chave de pagamento: </b-col>
              <b-col cols="6" class="mb-1" style="overflow-wrap: break-word"> 
                {{ expenses[0].payment_key }} 
              </b-col>
            </template>
            <template v-else-if="!rollingView">
              <b-col cols="6" class="mb-1"> Chave Pix: </b-col>
              <b-col cols="6" class="mb-1" style="overflow-wrap: break-word">
                {{ bill.consultant.franchise_pix }}
              </b-col>
              <b-col cols="6" class="mb-1"> Tipo da chave: </b-col>
              <b-col cols="6" class="mb-1 text-capitalize">
                {{
                  bill.consultant.franchise_pix
                    ? bill.consultant.franchise_pix_type_key
                    : ""
                }}
              </b-col>
            </template>
            <b-col cols="12" class="mb-1">
              <b-button
                v-if="bill.document_url && canManage && !rollingView"
                variant="link"
                size="md"
                no-caret
                style="padding: 0"
                @click="download(bill.id)"
              >
                Ver nota fiscal <feather-icon icon="DownloadIcon" />
              </b-button>
            </b-col>
            <b-col cols="6" class="mb-1"> Comentário: </b-col>
            <b-col cols="6" class="mb-1">
              <span v-safe-html="bill.comment"></span>
            </b-col>
          </b-row>
        </div>
        <div v-else class="d-flex justify-content-center m-2">
          <b-spinner variant="primary" />
        </div>
      </template>
    </b-sidebar>
  </div>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BButton,
  BFormInput,
  BFormTextarea,
  BCol,
  BRow,
  BSpinner,
  BLink,
} from "bootstrap-vue";
import { mapActions, mapMutations, mapGetters } from "vuex";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import Ripple from "vue-ripple-directive";
import * as types from "../store/types";
import {
  MANAGE_ACTION,
  FINANCIAL_MANAGMENT,
  FINANCIAL_ADJUSTMENTS,
  READ_ACTION,
} from "@/constants/resources";
import {
  ADMIN_CREDENTIALS_STORAGE_KEY,
  canAccessFinancialMenu,
} from "@/constants/auth";
import { getCredentials } from "@/helpers/credentials";
import { HUB_ID } from "@/constants/paying_companies";
import PaymentRollingSplit from "./PaymentRollingSplit";
import { buildRollingSplitView } from "../utils/rollingSplit";

export default {
  components: {
    PaymentRollingSplit,
    // BSV
    BButton,
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BCol,
    BRow,
    BSpinner,
    BLink,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      bill: undefined,
      expenses: undefined,
      rollingSplit: undefined,
    };
  },
  setup() {
    return { toast: useToast() };
  },
  computed: {
    ...mapGetters({
      billDetailsSidebar: types.BILL_DETAILS_SIDEBAR,
    }),
    invoiceTotal: function () {
      // ⚠️ Continua somando adjustments_amount, que segue sendo o TOTAL de ajustes. A invariante
      // é plataforma + hub + rolagem = adjustments_amount; trocar por uma das partes quebra o
      // "Total da fatura".
      return (
        this.bill.subscriptions_amount +
        this.bill.interviews_amount +
        this.bill.financial_solutions_amount +
        this.bill.adjustments_amount
      );
    },
    // NULL = saque anterior à separação por CNPJ; 0 = de fato zero. A diferença decide o layout,
    // então a checagem é por null/undefined e nunca por truthiness.
    hasPayingCompanySplit: function () {
      return (
        this.bill != null &&
        this.bill.platform_adjustments_amount !== null &&
        this.bill.platform_adjustments_amount !== undefined
      );
    },
    platformAdjustmentsAmount: function () {
      return Number(this.bill?.platform_adjustments_amount) || 0;
    },
    hubAdjustmentsAmount: function () {
      return Number(this.bill?.hub_adjustments_amount) || 0;
    },
    platformTotalAmount: function () {
      return (
        this.bill.subscriptions_amount +
        this.bill.interviews_amount +
        (this.hasPayingCompanySplit ? this.platformAdjustmentsAmount : 0)
      );
    },
    hubTotalAmount: function () {
      // Sem separação o total da Hub continua sendo solução financeira + TODOS os ajustes: é o
      // valor que aquele saque legado de fato pagou.
      return (
        this.bill.financial_solutions_amount +
        (this.hasPayingCompanySplit
          ? this.hubAdjustmentsAmount
          : this.bill.adjustments_amount)
      );
    },
    otherAdjustmentsAmount: function () {
      return (
        this.bill.adjustments_amount -
        this.platformAdjustmentsAmount -
        this.hubAdjustmentsAmount
      );
    },
    /**
     * Espelha `canViewFixedRoleItem` (@core/libs/acl/utils) e `canAccessFixedFinancialRoute`
     * (libs/acl/routeProtection), que espelham o middleware
     * `fixed_financial_menu_role:financial_adjustment:manage,financial_adjustment:read`:
     * role fixa do menu financeiro OU ability CASL no resource.
     *
     * ⚠️ Basta testar `read`: `manage` é curinga no CASL. E o 403 do backend cairia no
     * interceptor de `helpers/api.js`, que DESLOGA em 401/403/419 — link errado custa a sessão.
     */
    canOpenHubAdjustments: function () {
      if (!this.hasPayingCompanySplit || !this.bill?.consultant_id) return false;
      const credentials = getCredentials(ADMIN_CREDENTIALS_STORAGE_KEY);
      return (
        Boolean(canAccessFinancialMenu(credentials?.user?.user_role_id)) ||
        this.$can(READ_ACTION, FINANCIAL_ADJUSTMENTS)
      );
    },
    hubAdjustmentsRoute: function () {
      return {
        name: "financial-adjustments-list",
        // Nomes iguais aos de `searchRules()` no backend: a query string é espelho do corpo do
        // POST /api/financial_adjustments/search.
        query: {
          consultant: String(this.bill.consultant_id),
          paying_company_id: String(HUB_ID),
        },
      };
    },
    /**
     * Mesma regra do PaymentSidebar: só PN com separação por CNPJ e divisão gravada (solicitação
     * criada com a chave ligada). Antiga, colaborador e despesa ficam no layout de hoje.
     */
    rollingView: function () {
      if (!this.bill || this.isExpense || this.isEmployee || !this.hasPayingCompanySplit) {
        return null;
      }
      return buildRollingSplitView(this.bill, this.rollingSplit, { readonly: true });
    },
    isEmployee: function () {
      return this.bill && 
        this.bill.user && 
        this.bill.user.department_id && 
        this.bill.consultant_id
    },
    isExpense: function () {
      return this.expenses &&
        this.expenses.length > 0 &&
        !this.isEmployee
    },
    canManage: function () {
      return this.$can(MANAGE_ACTION, FINANCIAL_MANAGMENT);
    },
  },
  methods: {
    ...mapMutations({
      mutateBillDetailsSidebar: types.MUTATE_BILL_DETAILS_SIDEBAR,
    }),
    ...mapActions({
      getBillDetails: types.GET_BILL_DETAILS,
      downloadDocument: types.DOWNLOAD_DOCUMENT,
    }),
    onShow() {
      this.getBillDetails(this.billDetailsSidebar.id)
        .then((response) => {
          if (response.data.bill.id) {
            this.bill = response.data.bill;
            this.expenses = response.data.expenses;
            this.rollingSplit = response.data.rolling_split;
          } else {
            throw new DOMException("Report não encontrado.");
          }
        })
        .catch((err) => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: "Ocorreu um erro ao carregar os detalhes. Entre em contato com o setor de TI.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
          this.hide;
        });
    },
    download(id) {
      this.loading = true;
      this.downloadDocument(id)
        .then((response) => {
          setTimeout(() => {
            window.open(response.data, "_blank").focus();
          });
        })
        .catch((error) => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: `Ocorreu um erro ao fazer download. Entre em contato com o setor de TI.`,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    clear() {
      this.bill = undefined;
      this.rollingSplit = undefined;
    },
    adjustmentsRoute(payingCompanyId) {
      return {
        name: "financial-adjustments-list",
        // Mesmos nomes de `searchRules()` no backend, como em hubAdjustmentsRoute.
        query: {
          consultant: String(this.bill.consultant_id),
          paying_company_id: String(payingCompanyId),
        },
      };
    },
  },
};
</script>

<style lang="scss">
.sidebar-bottom {
  position: absolute;
  bottom: 0;
  width: -moz-available; /* WebKit-based browsers will ignore this. */
  width: -webkit-fill-available; /* Mozilla-based browsers will ignore this. */
  width: fill-available;
}
</style>
